import { FlexRow, MaterialIcon, useModal, VerticalDivider } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { memo } from 'react';
import { generatePath } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import {
  ASSET_INFO_ACTION,
  FOLLOW_ACTION,
  HISTORIC_REPORT_ACTION,
  SECONDARIES_ACTION,
  SECONDARIES_PAGE,
  SEND_COMMAND_ACTION,
  TRACKING_PAGE,
} from '../../constants/app';
import { ASSET_TYPE_PHONE } from '../../constants/asset';
import { useCamera } from '../../hooks/useCamera';
import { useStyles } from '../../hooks/useStyles';
import { apiParamsState, availableActionsState } from '../../storage/app';
import { CurrentState } from '../../types/currentState';
import CommandModal from '../commands/modal/CommandModal';
import HistoricReportModal from '../reports/modal/HistoricReportModal';
import { StyledTooltip } from '../ui/StyledTooltip';
import CurrentStateModal from './modal/CurrentStateModal';
import StreamingCaptureModal from './streaming/modal/StreamingCaptureModal';

type Props = {
  currentState: CurrentState;
  hiddenActions?: string[];
  forStreaming?: boolean;
};

function CurrentStateBasicFooter({ currentState, hiddenActions = [], forStreaming = false }: Props) {
  const { css, theme } = useStyles();
  const { zoomTo } = useCamera();
  const {
    openModal: openCurrentStateModal,
    closeModal: closeCurrentStateModal,
    showModal: showCurrentStateModal,
  } = useModal();
  const { openModal: openCommandModal, closeModal: closeCommandModal, showModal: showCommandModal } = useModal();
  const {
    openModal: openHistoricReportModal,
    closeModal: closeHistoricReportModal,
    showModal: showHistoricReportModal,
  } = useModal();
  const { showModal: showCaptureModal, closeModal: closeCaptureModal, openModal: openCaptureModal } = useModal();
  const apiParams = useRecoilValue(apiParamsState);
  const actions = useRecoilValue(availableActionsState);

  const lng = currentState.point.coordinates[0];
  const lat = currentState.point.coordinates[1];

  const actionIsVisible = (action: string) => {
    return !hiddenActions.includes(action);
  };

  return (
    <FlexRow
      classNames={css({
        alignItems: 'center',
        justifyContent: 'space-evenly',
        padding: theme.sizing.scale200,
      })}
    >
      {actionIsVisible(FOLLOW_ACTION) && actions.includes(FOLLOW_ACTION) && (
        <>
          <StyledTooltip
            content="Abrir seguimiento"
            showArrow={false}
            placement="bottom"
            ignoreBoundary
          >
            <Button
              shape="circle"
              kind="tertiary"
              size="compact"
              onClick={() =>
                apiParams &&
                window.open(
                  generatePath(TRACKING_PAGE.route, { id: apiParams.subClientId, assetId: currentState.asset._id }),
                  '_blank',
                  'directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no, height=700px, width=1400px'
                )
              }
            >
              <MaterialIcon name="open_in_new" />
            </Button>
          </StyledTooltip>
          <VerticalDivider />
        </>
      )}
      {actionIsVisible(SECONDARIES_ACTION) &&
        actions.includes(SECONDARIES_ACTION) &&
        !!currentState.asset.secondaries?.length && (
          <>
            <StyledTooltip
              content="Ver dispositivos secundarios"
              showArrow={false}
              placement="bottom"
              ignoreBoundary
            >
              <Button
                shape="circle"
                kind="tertiary"
                size="compact"
                onClick={() =>
                  apiParams &&
                  window.open(
                    generatePath(SECONDARIES_PAGE.route, {
                      id: apiParams.subClientId,
                      assetId: currentState.asset._id,
                    }),
                    '_blank',
                    'directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no, height=700px, width=1400px'
                  )
                }
              >
                <MaterialIcon name="devices_other" />
              </Button>
            </StyledTooltip>
            <VerticalDivider />
          </>
        )}
      {actionIsVisible(ASSET_INFO_ACTION) && actions.includes(ASSET_INFO_ACTION) && (
        <>
          <StyledTooltip
            content="Información del activo"
            showArrow={false}
            placement="bottom"
            ignoreBoundary
          >
            <Button
              shape="circle"
              kind="tertiary"
              size="compact"
              onClick={openCurrentStateModal}
            >
              <MaterialIcon name="info" />
            </Button>
          </StyledTooltip>
          <VerticalDivider />
        </>
      )}
      {actionIsVisible(HISTORIC_REPORT_ACTION) && actions.includes(HISTORIC_REPORT_ACTION) && (
        <>
          <StyledTooltip
            content="Solicitar histórico de posiciones"
            showArrow={false}
            placement="bottom"
            ignoreBoundary
          >
            <Button
              shape="circle"
              kind="tertiary"
              size="compact"
              onClick={openHistoricReportModal}
            >
              <MaterialIcon name="timeline" />
            </Button>
          </StyledTooltip>
          <VerticalDivider />
        </>
      )}
      {actionIsVisible(SEND_COMMAND_ACTION) &&
        actions.includes(SEND_COMMAND_ACTION) &&
        currentState.asset.assetType !== ASSET_TYPE_PHONE && (
          <>
            <StyledTooltip
              content="Enviar comando"
              showArrow={false}
              placement="bottom"
              ignoreBoundary
            >
              <Button
                shape="circle"
                kind="tertiary"
                size="compact"
                onClick={openCommandModal}
              >
                <MaterialIcon name="settings_remote" />
              </Button>
            </StyledTooltip>
            <VerticalDivider />
          </>
        )}
      {forStreaming && (
        <>
          <StyledTooltip
            content="Foto en tiempo real"
            showArrow={false}
            placement="bottom"
            ignoreBoundary
          >
            <Button
              shape="circle"
              kind="tertiary"
              size="compact"
              onClick={openCaptureModal}
            >
              <MaterialIcon name="image" />
            </Button>
          </StyledTooltip>
          <VerticalDivider />
        </>
      )}
      <StyledTooltip
        content="Encontrar en el mapa"
        showArrow={false}
        placement="bottom"
        ignoreBoundary
      >
        <Button
          shape="circle"
          kind="tertiary"
          size="compact"
          onClick={() => zoomTo({ lat, lng })}
        >
          <MaterialIcon name="location_searching" />
        </Button>
      </StyledTooltip>
      <CurrentStateModal
        onClose={closeCurrentStateModal}
        isOpen={showCurrentStateModal}
        ident={currentState.ident}
      />
      <CommandModal
        onClose={closeCommandModal}
        isOpen={showCommandModal}
        asset={currentState.asset}
      />
      <HistoricReportModal
        onClose={closeHistoricReportModal}
        isOpen={showHistoricReportModal}
        assetId={currentState.asset._id}
      />
      <StreamingCaptureModal
        asset={currentState.asset}
        isOpen={showCaptureModal}
        onClose={closeCaptureModal}
      />
    </FlexRow>
  );
}

export default memo(CurrentStateBasicFooter);
